import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { BusyLabel, CAPTION, swapFadeCss } from "src/domains/shared/components/StateTransition";
import { MYPAGE_DIRTY_DOT } from "../myPageStyles";

interface ChangeBarProps {
  changeCount: number;
  saving: boolean;
  onReset: () => void;
  onSave: () => void;
}

const BAR_HEIGHT_PX = 48;
const SAVE_MIN_WIDTH_PX = 64;

/**
 * 권한 변경 바 — 변경이 없어도 같은 자리에 머물고 문구와 버튼 활성만 바뀐다.
 * 변경이 생길 때 바가 새로 나타나며 아래 레이아웃을 미는 일을 막는다.
 */
export const ChangeBar: FC<ChangeBarProps> = ({ changeCount, saving, onReset, onSave }) => {
  const { t } = useTranslation("projects");
  const hasChanges = changeCount > 0;

  return (
    <Box
      sx={{
        height: BAR_HEIGHT_PX,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        backgroundColor: COLOR_CONSTANTS.common.white_states.main,
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
      }}
    >
      <Box aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {hasChanges ? (
          <Box key="dirty" css={swapFadeCss} sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: MYPAGE_DIRTY_DOT }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {t("myPageModal.permission.changeCount", { ns: "projects", count: changeCount })}
            </Typography>
          </Box>
        ) : (
          <Typography key="clean" css={swapFadeCss} sx={{ fontSize: 12, color: CAPTION }}>
            {t("myPageModal.permission.noChanges", { ns: "projects" })}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: "8px" }}>
        <Button
          size="small"
          emphasis="text"
          intent="inherit"
          disabled={!hasChanges || saving}
          onClick={onReset}
        >
          {t("projectPermission.resetChanges", { ns: "projects" })}
        </Button>
        <Button
          size="small"
          emphasis="contained"
          intent="primary"
          disabled={!hasChanges && !saving}
          aria-disabled={saving || undefined}
          onClick={saving ? undefined : onSave}
          sx={{ minWidth: SAVE_MIN_WIDTH_PX }}
        >
          <BusyLabel
            label={t("projectPermission.saveChanges", { ns: "projects" })}
            busy={saving}
            busyLabel={t("myPageModal.account.saving", { ns: "projects" })}
            spinnerSize={12}
          />
        </Button>
      </Box>
    </Box>
  );
};
